import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics35() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-34", 
        index: "/index-economics",                 
        next: "/economics-36", 
      };
        const titleText ="Why Inefficient Industries Should Be Allowed to Die";

        const questions = [
  {
    text: "1. Why do some policymakers argue for preventing new capital from entering certain industries?",
    options: [
      { value: "A", label: "A. Because new technologies are too expensive" },
      { value: "B", label: "B. To reduce international competition" },
      {
        value: "C",
        label: "C. Because the industry is claimed to be overcrowded",
      },
      { value: "D", label: "D. To allow for environmental restoration" },
    ],
    correct: "C",
  },
  {
    text: "2. What is a likely result of artificially sustaining an inefficient industry?",
    options: [
      { value: "A", label: "A. Increase in productivity across all sectors" },
      {
        value: "B",
        label: "B. Efficient reallocation of capital and labor",
      },
      {
        value: "C",
        label:
          "C. Wealth transfer from efficient industries to inefficient ones",
      },
      { value: "D", label: "D. Decrease in consumer prices" },
    ],
    correct: "C",
  },
  {
    text: "3. Which of the following is used as a justification to support industries through subsidies or protections?",
    options: [
      { value: "A", label: "A. Industries must always innovate" },
      {
        value: "B",
        label: "B. Higher average living costs must be avoided",
      },
      { value: "C", label: "C. Climate change denial" },
      { value: "D", label: "D. Global market monopoly" },
    ],
    correct: "B",
  },
  {
    text: "4. What is the suggested approach towards inefficient or dying industries?",
    options: [
      { value: "A", label: "A. Nationalization and bailouts" },
      { value: "B", label: "B. Strict tariffs on competing imports" },
      {
        value: "C",
        label: "C. Allowing them to naturally phase out",
      },
      {
        value: "D",
        label: "D. Increased labor protections and wage mandates",
      },
    ],
    correct: "C",
  },
];

 // State to track selected answers for all questions
       const [answers, setAnswers] = useState({});
     
       // Function to update an answer
       const handleAnswerChange = (questionIndex, selectedValue) => {
         setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
       };
     
       // Compute score
       const score = questions.reduce((total, q, index) => {
         return total + (answers[index] === q.correct ? 1 : 0);
       }, 0);

  return (
    <div className="container">
   <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={35}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />
      <p className="content">
        Welcome to <b>Day 35</b>! Today’s lesson challenges the idea that
        governments should keep inefficient or dying industries alive, showing
        how such interventions misallocate capital and labor, ultimately slowing
        down economic progress.
        <br /> In our previous lesson, we examined the arguments and outcomes
        surrounding price parity in the agricultural sector. We saw how
        well-intentioned policies, though often politically attractive, can
        result in distorted market signals, increased consumer burdens, and
        inefficient allocation of capital. Today, we extend our focus beyond
        agriculture to explore a wider pattern that afflicts numerous
        industries: the artificial sustenance of entire sectors under the
        pretext of national economic stability or social good. From steel to
        electric vehicles, various industries have become subjects of public
        policy debate, driven not by free-market logic but by emotionally and
        politically charged reasoning. In this lesson, we will unpack the
        rationale often presented for keeping certain industries alive, the
        tools used by governments to manipulate industrial survival, and the
        broader consequences these actions impose on the economy. As you read
        on, consider whether the industries we try so hard to save are genuinely
        essential or merely symbolic of a fear of change. <br />
        <br />
        <b>
          Manufacturing Dependency: The Justifications Behind Industrial
          Protection
        </b>
        <br />
        <br />
        The narrative that a particular industry is too important to fail has
        long been used to justify extensive government intervention.
        Historically, these arguments have spanned various sectors, from
        agriculture and mining to manufacturing and energy. This line of
        reasoning isn't new, but it has evolved over time to reflect
        contemporary concerns. In earlier decades, the steel industry was deemed
        vital to national defense and industrial self-sufficiency. Today,
        similar arguments are made for industries like electric vehicles (EVs)
        in the name of combating climate change. Regardless of the industry, the
        underlying message is the same: without governmental support, the
        collapse or absence of this industry will trigger a broader economic or
        environmental disaster.
        <br />
        To maintain these industries, policymakers deploy a wide arsenal of
        interventions. Tariff protections may be selectively removed to make an
        industry more competitive internationally or to grant it easier access
        to raw materials. In other cases, governments offer direct subsidies,
        either in the form of tax breaks or direct financial assistance. Carbon
        credits and emissions allowances serve as another tool, often
        functioning as hidden subsidies to favored industries under the guise of
        environmental policy. Yet another strategy includes limiting new capital
        investment in "overcrowded" sectors, allegedly to prevent market
        saturation. Each of these interventions aims to redirect or control
        capital and labor flows in a way that supposedly benefits the economy as
        a whole. However, these policies often ignore the complex and dynamic
        nature of markets and inadvertently result in the misallocation of
        scarce resources.
        <br />
        <br />
        For instance, the electric vehicle sector has seen aggressive government
        promotion globally, not only through direct consumer incentives but also
        via carbon credit programs that reward companies for reducing emissions,
        even if those reductions are not economically efficient. Meanwhile,
        traditional internal combustion engine manufacturers are penalized, not
        for inefficiency, but for failing to align with the ideological vision
        of environmental progress. This results in a skewed playing field where
        success is no longer dictated by consumer preference or cost-efficiency
        but by alignment with political narratives. Similar concerns apply to
        industries such as solar energy, high-speed rail, and even semiconductor
        manufacturing, where national pride and security concerns are used to
        justify continuous inflows of public funds.
        <br />
        <br />
        <b>The Myth of Overcrowding and Wage Stability</b>
        <br />
        <br />
        Among the most commonly cited reasons for limiting or controlling
        investment in specific industries is the claim that those sectors are
        "overcrowded." The logic is presented as follows: if too many businesses
        enter the same field, competition becomes excessive, profits vanish, and
        the market becomes unsustainable. Therefore, it is argued, government
        intervention is required to prevent additional capital from flowing into
        such sectors. While this rationale may seem sound at first glance, it
        fails to account for the self-regulating mechanisms of the free market.
        In reality, if an industry becomes truly overcrowded, its declining
        profit margins will serve as a natural deterrent for future investment.
        Investors, always on the lookout for better returns, will pivot away
        from saturated markets and reallocate capital to sectors with higher
        growth potential. Thus, intervention becomes not just unnecessary, but
        counterproductive.
        <br />
        When governments step in to limit capital flows based on politically
        motivated assessments of overcrowding, the result is a distortion of
        entrepreneurial signals. This compels individuals and businesses to
        redirect their investments toward less profitable or politically favored
        ventures. Instead of allowing market dynamics to identify and reward
        efficient industries, policymakers inadvertently suppress innovation and
        risk-taking. Moreover, such interventions may create perverse
        incentives, where industries lobby for their competitors to be labeled
        as overcrowded, thereby reducing future competition. This politicization
        of investment strategy does more harm than good and often leaves
        promising sectors underfunded while outdated or inefficient industries
        continue to consume public resources.
        <br />
        <br />A second justification often put forth in defense of industrial
        intervention is the preservation of wage stability and standard of
        living. The argument goes that if a particular sector suffers a decline,
        wages in that industry will fall, thereby reducing the purchasing power
        of its workers and increasing average living costs. Once again, this
        claim, while emotionally resonant, is economically misleading. While
        artificially propping up wages in one sector may temporarily improve
        conditions for a specific group of workers, it comes at a cost to
        others. The broader economy experiences wealth redistribution, where
        labor and capital are driven out of more efficient industries and into
        less efficient ones, solely to maintain wage levels in politically
        favored sectors.
        <br />
        This form of intervention disrupts the natural ebb and flow of economic
        evolution. Industries that could thrive under less regulation are
        throttled to maintain the status quo elsewhere. Additionally,
        artificially sustained wage levels in one sector could lead to
        inflationary pressures, as prices rise to reflect the increased cost of
        production, ultimately diminishing the very purchasing power such
        policies claim to protect. When looked at holistically, these wage and
        living cost justifications reveal themselves as zero-sum games i.e what
        is gained in one pocket is lost in another, often with compounding
        inefficiencies that drag the entire economy downward.
        <br />
        <br />
        <b>Creative Destruction and Economic Renewal</b>
        <br />
        <br />
        Perhaps the most fundamental error in the argument for industrial
        protection is the belief that every existing industry must be preserved.
        This thinking ignores a basic economic principle i.e creative
        destruction. Creative destruction refers to the process by which
        outdated or inefficient industries give way to newer, more efficient
        ones. This dynamic is not a sign of economic failure, but rather a
        hallmark of a healthy and adaptive market system. For progress to occur,
        resources must flow from dying industries to emerging ones. This may be
        painful in the short term, but it is essential for long-term prosperity.
        <br />
        Consider the example of the horse-and-buggy industry. Had governments in
        the early 20th century chosen to protect it under the pretext of job
        preservation or cultural heritage, the development and proliferation of
        the automobile industry might have been severely hindered. If we insist
        on keeping inefficient manufacturing industries alive through public
        funds, we forfeit the opportunity to lead in high-tech or
        service-oriented fields. The lesson is clear: artificially propping up
        industries simply because they exist or once thrived does not serve the
        greater economic good.
        <br />
        <br />
        Letting inefficient industries die is not an act of negligence but one
        of strategic reallocation. Capital and labor should be free to move
        toward industries that offer higher productivity and greater consumer
        value. Intervening in this process freezes economic progress, forcing
        entire economies to bear the costs of nostalgia and political
        convenience. Moreover, sustaining declining industries prevents the
        formation of new businesses that could employ those displaced, resulting
        in long-term unemployment and underemployment in communities that might
        otherwise have evolved and thrived. By clinging to outdated models, we
        not only waste capital but also suppress human potential.
        <br />
        The industrial interventionist mindset stems from a failure to
        understand or accept the fluid nature of economic life. All industries,
        no matter how central they seem today, are subject to the same forces of
        change and evolution. Attempts to preserve them indefinitely, whether in
        the name of economic stability, environmental sustainability, or social
        equity, often do more harm than good. A healthy economy requires the
        freedom to innovate, fail, and grow anew. When governments manipulate
        the market in favor of particular sectors, they shackle the very
        creativity and adaptability that drive long-term prosperity.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        In today's lesson, we explored the widespread yet misguided practice of
        propping up failing or inefficient industries through government
        interventions. We examined how subsidies, tariff exemptions, carbon
        credits, and capital restrictions are often justified using fear-based
        arguments such as the supposed collapse of the entire economy if a
        legacy industry like steel declines, or the promise of environmental
        salvation through emerging sectors like electric vehicles. These
        policies frequently stem from flawed assumptions: that industries are
        too overcrowded for new capital, or that living standards will decline
        without protectionist measures. In reality, capital and labor naturally
        flow away from unprofitable sectors unless artificially held back, and
        forcing them into less productive areas causes a net loss to society. We
        concluded that letting inefficient industries die is not only healthy
        but necessary for progress, as it frees up resources for more innovative
        and efficient alternatives.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        As we move forward, Day 36 will deepen your understanding of how prices
        are not arbitrarily set, nor are they determined by manufacturing costs
        alone. You will learn how prices in a free economy are shaped by the
        dynamic interaction of supply and demand, how these prices influence
        production decisions, and why the price system is the most efficient way
        to guide the movement of capital and labor across industries. This
        foundational concept is vital for anyone who wants to grasp the real
        forces that sustain or shut down industries in a market economy.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics35;
